import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Icon,
  Button,
  Input,
  FormControl,
  FormLabel,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Spinner,
  Alert,
  AlertIcon,
  AlertDescription,
  IconButton,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  useColorModeValue,
  useToast,
  Switch,
  Divider,
} from '@chakra-ui/react';
import {
  FiPlus,
  FiEdit3,
  FiTrash2,
  FiRefreshCw,
  FiAlertTriangle,
  FiActivity,
  FiFileText,
} from 'react-icons/fi';
import {
  listStudentStatuses,
  upsertStudentStatus,
  deleteStudentStatus,
  getStudentsCache,
} from 'services/qrService';
import { getSurveyResponse } from 'services/surveyService';
import StudentPicker from './StudentPicker';
import StaffPicker from 'components/Search/StaffPicker';
import GuardianPicker from 'components/Search/GuardianPicker';
import ModalSurveyResponse from 'components/Modal/ModalSurveyResponse';

const SICK_EXIT_TITLES = ['Accidente escolar', 'Enfermedad', 'Otros'];

const EMPTY_FORM = {
  id: null,
  student: null, // { id, username, name, meta_data }
  reason: SICK_EXIT_TITLES[1],
  date: '',
  responsible_name: null, // persona elegida vía GuardianPicker
  authorized_by: null,    // persona elegida vía StaffPicker
  active: true,
};

const toDateOnly = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' });
};

// Tab dedicado — mismo patrón que PedagogicalExitsTab.js, pero para Salida
// por Enfermedad, que hasta ahora solo vivía mezclada dentro del tab
// genérico "Estados de Alumnos" (StudentStatusesTab.js). Reusa el mismo
// endpoint /qr/statuses filtrado a type=sick_exit — no hay tabla propia.
const SickExitsTab = () => {
  const [statuses, setStatuses] = useState([]);
  const [namesByRut, setNamesByRut] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isFichaOpen, setIsFichaOpen] = useState(false);
  const [fichaData, setFichaData] = useState(null);

  const { isOpen: isFormOpen, onOpen: onOpenForm, onClose: onCloseForm } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onOpenDelete, onClose: onCloseDelete } = useDisclosure();

  const toast = useToast();
  const mutedColor = useColorModeValue('gray.500', 'gray.400');

  const fetchStatuses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listStudentStatuses({ type: 'sick_exit' });
      setStatuses(res.data ?? []);
    } catch {
      setError('No se pudo cargar la lista de salidas por enfermedad.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatuses(); }, [fetchStatuses]);

  // Carga liviana de nombres para mostrar en la tabla (cache de alumnos con token QR)
  useEffect(() => {
    getStudentsCache(1, 200)
      .then(res => {
        const map = {};
        (res.data ?? []).forEach(s => { map[s.rut] = s.nombre; });
        setNamesByRut(map);
      })
      .catch(() => {});
  }, []);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditing(null);
    onOpenForm();
  };

  const openEdit = (status) => {
    setForm({
      id: status.id,
      // El registro solo guarda rut/wp_user_id — se sintetiza un objeto
      // mínimo para que StudentPicker lo muestre como "seleccionado"; sin
      // meta_data acá, GuardianPicker no podrá ofrecer el apoderado
      // registrado hasta que se busque/cambie el alumno de nuevo.
      student: {
        id: status.wp_user_id,
        username: status.rut,
        name: namesByRut[status.rut] ?? status.rut,
      },
      reason: status.reason || SICK_EXIT_TITLES[1],
      date: toDateOnly(status.starts_at),
      responsible_name: status.responsible_name ? { name: status.responsible_name } : null,
      authorized_by: status.authorized_by ? { name: status.authorized_by } : null,
      active: status.active,
    });
    setEditing(status.id);
    onOpenForm();
  };

  const handleSave = async () => {
    if (!form.student) {
      toast({ title: 'Selecciona un alumno', status: 'warning', duration: 2500 });
      return;
    }
    if (!form.date) {
      toast({ title: 'Selecciona la fecha', status: 'warning', duration: 2500 });
      return;
    }
    setSaving(true);
    try {
      const res = await upsertStudentStatus({
        id: form.id,
        wp_user_id: form.student.id,
        rut: form.student.username,
        type: 'sick_exit',
        reason: form.reason,
        starts_at: `${form.date}T00:00:00`,
        ends_at: `${form.date}T23:59:59`,
        authorized_by: form.authorized_by?.name?.trim() || null,
        responsible_name: form.responsible_name?.name?.trim() || null,
        active: form.active,
      });
      toast({ title: editing ? 'Salida actualizada' : 'Salida creada', status: 'success', duration: 2500 });
      if (res?.data?.deactivated?.length > 0) {
        toast({
          title: 'Estado anterior desactivado',
          description: 'El alumno ya tenía otro estado vigente — se desactivó automáticamente (solo puede tener uno a la vez).',
          status: 'info', duration: 5000,
        });
      }
      onCloseForm();
      fetchStatuses();
    } catch (e) {
      toast({
        title: 'Error al guardar',
        description: e?.response?.data?.errors ? JSON.stringify(e.response.data.errors) : e.message,
        status: 'error', duration: 4000,
      });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (status) => {
    setDeleteTarget(status);
    onOpenDelete();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(deleteTarget.id);
    try {
      await deleteStudentStatus(deleteTarget.id);
      toast({ title: 'Salida eliminada', status: 'success', duration: 2500 });
      onCloseDelete();
      fetchStatuses();
    } catch {
      toast({ title: 'Error al eliminar', status: 'error', duration: 3000 });
    } finally {
      setDeleting(null);
    }
  };

  // "Ver ficha" — mismo modal con cabecera/logo/PDF que ya se usa en Perfil
  // > Fichas y en el resumen del Scanner. Salida por Enfermedad siempre
  // firma una sola ficha por salida (a diferencia de Pedagógica), así que
  // acá no hace falta el menú de "elegir cuál".
  const handleOpenFicha = (responseId) => {
    setFichaData(null);
    setIsFichaOpen(true);
    getSurveyResponse(responseId)
      .then(setFichaData)
      .catch(() => setFichaData({ error: 'No se pudieron cargar los datos de la ficha' }));
  };

  const setF = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  return (
    <Box fontFamily="jakarta">
      <Flex justify="space-between" align="center" mb={4} gap="10px" flexWrap="wrap">
        <Box>
          <Text fontSize="18px" fontWeight="800" color="ink.900" letterSpacing="-0.02em">Salidas por Enfermedad</Text>
          <Text fontSize="13px" color="ink.400" fontWeight="500" mt="2px">
            {statuses.length} salida{statuses.length !== 1 ? 's' : ''} registrada{statuses.length !== 1 ? 's' : ''} · {statuses.filter(s => s.active).length} activa{statuses.filter(s => s.active).length !== 1 ? 's' : ''}
          </Text>
        </Box>
        <HStack spacing="8px">
          <Tooltip label="Refrescar">
            <IconButton
              icon={<FiRefreshCw />}
              h="38px" w="38px" borderRadius="11px"
              border="1.5px solid" borderColor="cream.line2"
              bg="white" color="ink.700"
              _hover={{ borderColor: 'coral.500', color: 'coral.500' }}
              onClick={fetchStatuses} isLoading={loading} aria-label="Refrescar"
            />
          </Tooltip>
          <Button
            h="38px" px={4} borderRadius="11px"
            bg="coral.500" color="#fff" fontWeight="700" fontSize="13px"
            _hover={{ bg: 'coral.600' }}
            boxShadow="0 8px 18px -8px rgba(239,90,42,.65)"
            leftIcon={<FiPlus />}
            onClick={openCreate}
          >
            Nueva salida
          </Button>
        </HStack>
      </Flex>

      <Box bg="white" borderRadius="22px" overflow="hidden" border="1px solid" borderColor="cream.line"
        boxShadow="0 2px 12px -4px rgba(35,30,26,.08)">
        {error && (
          <Alert status="error" m={4} borderRadius="10px">
            <AlertIcon />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loading ? (
          <VStack py={12} spacing={3}>
            <Spinner size="lg" color="primary.500" />
            <Text fontSize="sm" color={mutedColor}>Cargando salidas por enfermedad…</Text>
          </VStack>
        ) : statuses.length === 0 ? (
          <VStack py={12} spacing={2}>
            <Icon as={FiActivity} color={mutedColor} boxSize={8} />
            <Text color={mutedColor} fontSize="sm">Sin salidas por enfermedad registradas</Text>
          </VStack>
        ) : (
          <TableContainer overflowX="auto">
            <Table size="sm" variant="unstyled">
              <Thead>
                <Tr bg="cream.bg2" borderBottom="1px solid" borderColor="cream.line">
                  <Th py={3} fontSize="11px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing=".05em">Alumno</Th>
                  <Th py={3} fontSize="11px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing=".05em">Título</Th>
                  <Th py={3} fontSize="11px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing=".05em" display={{ base: 'none', md: 'table-cell' }}>Responsable</Th>
                  <Th py={3} fontSize="11px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing=".05em" display={{ base: 'none', lg: 'table-cell' }}>Fecha</Th>
                  <Th py={3} fontSize="11px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing=".05em" display={{ base: 'none', sm: 'table-cell' }}>Estado</Th>
                  <Th />
                </Tr>
              </Thead>
              <Tbody>
                {statuses.map(status => (
                  <Tr key={status.id}
                    _hover={{ bg: 'coral.tint' }} transition="background .13s"
                    opacity={status.active ? 1 : 0.5}
                    borderTop="1px solid" borderColor="cream.line"
                  >
                    <Td py={3} maxW="180px">
                      <Text fontWeight="700" fontSize="14px" color="ink.900" noOfLines={1}>
                        {namesByRut[status.rut] ?? status.rut}
                      </Text>
                      <Text fontSize="11.5px" color="ink.400" fontWeight="500">RUT {status.rut}</Text>
                    </Td>
                    <Td py={3}>
                      <HStack
                        as="span" display="inline-flex" align="center" spacing={1}
                        px="10px" py="4px" borderRadius="999px"
                        bg="#CCFBF1" color="#0F766E"
                        fontSize="11px" fontWeight="800"
                      >
                        <Icon as={FiActivity} boxSize={3} />
                        <Text>{status.reason || 'Enfermedad'}</Text>
                      </HStack>
                    </Td>
                    <Td py={3} fontSize="13.5px" color="ink.700" fontWeight="500" display={{ base: 'none', md: 'table-cell' }} maxW="180px">
                      <Text noOfLines={1}>{status.responsible_name || '—'}</Text>
                    </Td>
                    <Td py={3} fontSize="13px" color="ink.400" display={{ base: 'none', lg: 'table-cell' }}>
                      {formatDate(status.starts_at)}
                    </Td>
                    <Td py={3} display={{ base: 'none', sm: 'table-cell' }}>
                      <HStack
                        as="span" display="inline-flex"
                        px="10px" py="4px" borderRadius="999px"
                        bg={status.active ? '#DCF1E6' : 'cream.bg2'}
                        color={status.active ? '#1E7A4E' : 'ink.400'}
                        fontSize="12px" fontWeight="700"
                      >
                        <Text>{status.active ? 'Activo' : 'Inactivo'}</Text>
                      </HStack>
                    </Td>
                    <Td py={3} isNumeric>
                      <HStack spacing="6px" justify="flex-end">
                        {status.survey_responses?.length > 0 && (
                          <Tooltip label="Ver ficha">
                            <IconButton
                              icon={<FiFileText />} w="32px" h="32px" minW="32px" borderRadius="9px"
                              bg="mint.bg" color="mint.ink" border="none"
                              _hover={{ bg: '#B4EEDD' }}
                              onClick={() => handleOpenFicha(status.survey_responses[0].id)}
                              aria-label="Ver ficha"
                            />
                          </Tooltip>
                        )}
                        <Tooltip label="Editar">
                          <IconButton
                            icon={<FiEdit3 />} w="32px" h="32px" minW="32px" borderRadius="9px"
                            bg="cream.bg2" color="ink.400" border="none"
                            _hover={{ bg: 'coral.soft', color: 'coral.700' }}
                            onClick={() => openEdit(status)} aria-label="Editar"
                          />
                        </Tooltip>
                        <Tooltip label="Eliminar">
                          <IconButton
                            icon={<FiTrash2 />} w="32px" h="32px" minW="32px" borderRadius="9px"
                            bg="cream.bg2" color="danger" border="none"
                            _hover={{ bg: '#FEE2E2' }}
                            onClick={() => confirmDelete(status)}
                            isLoading={deleting === status.id} aria-label="Eliminar"
                          />
                        </Tooltip>
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* ── Modal crear / editar ── */}
      <Modal isOpen={isFormOpen} onClose={onCloseForm} isCentered size="md" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent mx={3}>
          <ModalHeader>{editing ? 'Editar salida por enfermedad' : 'Nueva salida por enfermedad'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel fontSize="sm">Alumno</FormLabel>
                <StudentPicker value={form.student} onSelect={(s) => setForm(f => ({ ...f, student: s, responsible_name: null }))} />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm">Título</FormLabel>
                <Select size="sm" borderRadius="8px" value={form.reason} onChange={setF('reason')}>
                  {SICK_EXIT_TITLES.map(t => <option key={t} value={t}>{t}</option>)}
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm">Fecha</FormLabel>
                <Input type="date" size="sm" borderRadius="8px"
                  value={form.date} onChange={setF('date')} />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Responsable (quien retira)</FormLabel>
                <GuardianPicker metaData={form.student?.meta_data} value={form.responsible_name}
                  onSelect={(p) => setForm(f => ({ ...f, responsible_name: p }))} />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Autorizado por</FormLabel>
                <StaffPicker value={form.authorized_by}
                  onSelect={(p) => setForm(f => ({ ...f, authorized_by: p }))}
                  placeholder="Buscar quien autoriza…" />
              </FormControl>

              <Divider />

              <FormControl display="flex" alignItems="center" gap={3}>
                <FormLabel mb={0} fontSize="sm">Activo</FormLabel>
                <Switch isChecked={form.active}
                  onChange={e => setForm(f => ({ ...f, active: e.target.checked }))}
                  colorScheme="secondary" />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" size="sm" onClick={onCloseForm}>Cancelar</Button>
            <Button variant="educational-primary" size="sm" onClick={handleSave} isLoading={saving}>
              {editing ? 'Actualizar' : 'Crear salida'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ── Modal eliminar ── */}
      <Modal isOpen={isDeleteOpen} onClose={onCloseDelete} isCentered size="sm">
        <ModalOverlay />
        <ModalContent mx={3}>
          <ModalHeader>Eliminar salida</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <HStack mb={3} spacing={3}>
              <Icon as={FiAlertTriangle} color="red.500" boxSize={6} />
              <Text fontWeight="semibold">¿Eliminar esta salida por enfermedad?</Text>
            </HStack>
            <Text fontSize="sm" color={mutedColor}>
              Esta acción no se puede deshacer.
            </Text>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" size="sm" onClick={onCloseDelete}>Cancelar</Button>
            <Button colorScheme="red" size="sm" onClick={handleDelete} isLoading={!!deleting}>
              Sí, eliminar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ModalSurveyResponse
        isOpen={isFichaOpen}
        onClose={() => setIsFichaOpen(false)}
        customer={{ first_name: fichaData?.survey?.name ?? '', last_name: '', username: '' }}
        data={fichaData}
      />
    </Box>
  );
};

export default SickExitsTab;
